<template>
  <div
    class="h-full publication-editor-step"
    :class="{ 'is-fullscreen': isFullscreen, 'is-fullscreen-desktop': isFullscreen && previewDevice === 'desktop' }"
    data-testid="oc-publication-editor-step">
    <div class="resizable-container d-flex flex-row flex-grow-1">
      <div v-show="!isFullscreen" class="left-column" ref="leftColumn">
        <div class="sticky-tabs-header" data-testid="oc-publication-editor-tabs">
          <Tabs v-model="currentTab" :tabs="orderTabs" class="nav-bordered px-3 mt-2 bg-white" />
        </div>
        <div
          v-show="currentTab === 'form'"
          class="pb-4 px-4 overflow-auto"
          data-testid="oc-publication-editor-form-panel">
          <!-- lead funnels only: an application form always collects both an email address and a
               phone number, so the backend stores `both` regardless and there is nothing to choose -->
          <div v-if="publication && publication.funnel_enabled" class="form-editor-section">
            <button
              type="button"
              class="section-header"
              data-testid="oc-publication-editor-section-contact"
              :aria-expanded="formSectionExpanded.contact"
              @click="toggleFormSection('contact')">
              <div class="section-header-text">
                <h3 class="mb-1">{{ $t('publication.editor.contact_method') }}</h3>
                <small class="text-muted">{{ $t('publication.editor.contact_method.description') }}</small>
              </div>
              <i
                class="ti section-header-chevron"
                :class="formSectionExpanded.contact ? 'ti-chevron-up' : 'ti-chevron-down'"></i>
            </button>

            <div v-show="formSectionExpanded.contact" class="section-body">
              <LiFormSelect
                :label="$t('publication.editor.contact_method')"
                :options="contactMethodOptions"
                :modelValue="publication.form_contact_method"
                @update:modelValue="updateContactMethod" />
            </div>
          </div>

          <TemplateOptionsForm
            v-if="publication?.template_values && formTemplateOptions.length"
            ref="formOptionsFormRef"
            :option-defs="formTemplateOptions"
            :company-id="companyId"
            v-model="templateOptions"
            class="form-options"
            @option-change="onOptionChange" />
        </div>
        <!-- keep questions mounted across tabs so drafts and preview data stay available -->
        <div
          v-if="publication"
          v-show="currentTab === 'questions'"
          class="questions-panel overflow-auto"
          data-testid="oc-publication-editor-questions-panel">
          <QuestionEditor
            ref="questionEditorRef"
            :publication-id="publication.pubkey!"
            @questions-updated="(qs) => (formQuestions = qs)"
            @questions-loading="(loading) => (questionsLoading = loading)" />
        </div>
        <div
          v-if="currentTab === 'settings'"
          class="px-4 overflow-auto"
          data-testid="oc-publication-editor-settings-panel">
          <template v-if="publication?.template_values && settingsTemplateOptions.length">
            <TemplateOptionsForm
              ref="settingsOptionsFormRef"
              :option-defs="settingsTemplateOptions"
              :company-id="companyId"
              v-model="templateOptions"
              @option-change="onOptionChange" />
          </template>
          <template v-else>
            <h4>{{ $t('publication.editor.no_settings') }}</h4>
          </template>
        </div>
      </div>
      <div v-show="!isFullscreen" class="divider" @mousedown="startDragging">
        <div class="divider-handle" v-tooltip="$t('publication.editor.resize')">
          <IconGripVertical :size="14" />
        </div>
      </div>
      <div class="scrollable-column right-column" ref="rightColumn">
        <div class="iframe-container w-100 h-100">
          <div class="publication-content w-100 h-100">
            <PublicationToolbar
              v-model:mode="popsMode"
              v-model:device="previewDevice"
              :fullscreen="isFullscreen"
              @open-fullscreen="openFullscreen"
              @close-fullscreen="closeFullscreen"
              @preview="onPreview" />
            <div class="publication-preview-canvas" ref="previewCanvas">
              <!-- withheld until the adjustment prompt (if any) resolves, so the preview opens with the
                 chosen adjustment already applied instead of loading defaults and reloading afterwards -->
              <div v-if="publication?.template && !templateReady" class="publication-preview-loading">
                <span class="spinner-border" role="status" aria-hidden="true" />
              </div>
              <div
                v-else-if="publication?.template"
                class="publication-preview-iframe"
                :style="previewFrameStyle"
                ref="publicationIframe">
                <PopsHostFrame
                  ref="popsHostRef"
                  :template-key="publication.template"
                  :variation="publication.variation ?? undefined"
                  :locale="orderCreationStore.order.language"
                  :mode="popsMode"
                  :options="templateOptions"
                  :publication="publicationState"
                  :form-questions="formQuestions"
                  :contact-method="publication?.form_contact_method"
                  :interaction-modules="interactionModules"
                  :parameters="popsParameters"
                  @update:options="onClientOptionsUpdate" />
                <!-- after the iframe so the badge stacks above the template's loading mask;
                   the iframe is a replaced element and paints over earlier siblings even with z-index -->
                <Transition name="optimize-overlay">
                  <div v-if="isOptimizingContent" class="optimize-content-overlay">
                    <div class="optimize-content-indicator">
                      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      {{ $t('publication.editor.transforming_content') }}
                    </div>
                  </div>
                </Transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AdjustmentSaveModal
      v-model:dialogShow="showSaveAdjustmentModal"
      :option-defs="persistableOptionDefs"
      :values="templateOptions"
      :default-name="suggestedAdjustmentName"
      @save="onSaveAdjustment"
      @skip="showSaveAdjustmentModal = false"
      @close="onCloseAdjustmentModal" />

    <AdjustmentLoadModal
      v-model:dialogShow="showLoadAdjustmentModal"
      :adjustments="savedAdjustments"
      :option-defs="persistableOptionDefs"
      @load="onLoadAdjustment"
      @delete="onDeleteAdjustment" />
  </div>
</template>
<script lang="ts" setup>
import { useOrderCreationStore } from '@/stores/orderCreation';
import { FormSelect as LiFormSelect, Tabs } from '@prospective/lithium';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import translator from '@/locale/translator';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import {
  getPublicationTemplate,
  updateCustomerPublication,
  fetchInteractionModules,
  getPublicationTaskStatus,
} from '@/api/publication';
import { fetchCompanyImages } from '@/api/company';
import { isFormTemplateOption } from '@/autopilot/functions/publications/describeTemplateFields';
import {
  useAutopilotPublicationEditor,
  type QuestionMutationResult,
} from '@/composables/useAutopilotPublicationEditor';
import { useAutopilotRouteReady } from '@/composables/useAutopilotRouteReady';
import { useAutopilotWizardStep } from '@/composables/useAutopilotWizardStep';
import type { FormQuestionInput } from '@/types/formQuestion';
import QuestionEditor from '@/components/form/QuestionEditor.vue';
import { IconGripVertical } from '@tabler/icons-vue';
import TemplateOptionsForm from './subcomponents/TemplateOptionsForm.vue';
import type { TemplateMeta, TemplateOptionDef } from '@/types/templateMeta.js';
import type { IFormQuestion } from '@/types/formQuestion';
import type { FormContactMethod } from '@/types/publication';
import type { IPublicationAdjustment } from '@/types/publicationAdjustment';
import PopsHostFrame from './subcomponents/PopsHostFrame.vue';
import PublicationToolbar from './subcomponents/PublicationToolbar.vue';
import AdjustmentSaveModal from './subcomponents/AdjustmentSaveModal.vue';
import AdjustmentLoadModal from './subcomponents/AdjustmentLoadModal.vue';
import { defaultAdjustmentName, hasChangedFromBaseline } from './subcomponents/adjustmentHelpers';
import {
  createPublicationAdjustment,
  deletePublicationAdjustment,
  fetchPublicationAdjustments,
  resolveAdjustmentChannel,
} from '@/api/publicationAdjustment';

const route = useRoute();
const toast = useToast();

const allowNext = defineModel('allowNext', { type: Boolean, required: true });
const orderCreationStore = useOrderCreationStore();
const { publication, publicationSavingState } = storeToRefs(orderCreationStore);
const companyId = computed(() => publication.value?.company_id ?? undefined);

const popsHostRef = ref<InstanceType<typeof PopsHostFrame> | null>(null);
const formOptionsFormRef = ref<InstanceType<typeof TemplateOptionsForm> | null>(null);
const settingsOptionsFormRef = ref<InstanceType<typeof TemplateOptionsForm> | null>(null);
const questionEditorRef = ref<InstanceType<typeof QuestionEditor> | null>(null);
const questionsLoading = ref(false);
const companyImages = ref<Array<{ hash: string; url: string }> | null>(null);
const routeReady = useAutopilotRouteReady();

const currentTab = ref<'questions' | 'form' | 'settings'>('settings');
const previewDevice = ref<'mobile' | 'tablet' | 'desktop'>('desktop');
const previewCanvas = ref<HTMLElement | null>(null);
const previewCanvasWidth = ref(0);
const previewCanvasHeight = ref(0);
const publicationIframe = ref<HTMLElement | null>(null);
let saveDebounce: ReturnType<typeof setTimeout> | null = null;
let reloadDebounce: ReturnType<typeof setTimeout> | null = null;
let formTaskPollId: ReturnType<typeof setTimeout> | null = null;
const isOptimizingContent = ref(false);
const optimizingPhase = ref<'transform_content'>('transform_content');
const templateMeta = ref<TemplateMeta>({} as TemplateMeta);
const templateOptions = ref<Record<string, unknown>>({});
// gates the preview: false while defaults are computed and any adjustment prompt is pending
const templateReady = ref(false);
const interactionModules = ref<Record<string, any> | null>(null);
const popsMode = ref<'editor' | 'preview'>('editor');
const isFullscreen = ref(false);
let previousPreviewMode: 'editor' | 'preview' = 'editor';
let previousPreviewDevice: 'mobile' | 'tablet' | 'desktop' = 'desktop';
let previousBodyOverflow = '';
const formQuestions = ref<IFormQuestion[]>([]); // populated when QuestionEditor emits its loaded list
const contactMethodOptions = computed(() => [
  { label: translator.get('publication.editor.contact_method.email'), value: 'email' },
  { label: translator.get('publication.editor.contact_method.phone'), value: 'phone' },
  { label: translator.get('publication.editor.contact_method.both'), value: 'both' },
]);
const formTemplateOptions = computed(() => filterTemplateOptions(isFormTemplateOption));
const settingsTemplateOptions = computed(() =>
  filterTemplateOptions((def, header) => !isFormTemplateOption(def, header))
);
const formSectionExpanded = ref<Record<'contact', boolean>>({
  contact: true,
});

// template adjustments (saved, reusable option values scoped to a channel)
const showSaveAdjustmentModal = ref(false);
const showLoadAdjustmentModal = ref(false);
const savedAdjustments = ref<IPublicationAdjustment[]>([]);
// resolves the pending confirmAdjustmentsBeforeLeave() promise once the save prompt is dismissed
let saveAdjustmentResolver: ((proceed: boolean) => void) | null = null;
// resolves the pending maybePromptLoadAdjustments() promise once the load prompt closes
let loadAdjustmentResolver: ((applied: Record<string, unknown>) => void) | null = null;
let pendingAppliedAdjustment: Record<string, unknown> = {};
// keys the user explicitly chose via a loaded adjustment — the transform/optimize content poll
// below must not clobber these with freshly generated defaults once it resolves
let adjustmentAppliedKeys = new Set<string>();
// snapshot to diff templateOptions against when deciding whether to offer the save prompt: starts
// empty (an untouched template never has anything worth persisting) and is replaced wholesale by
// whatever was last loaded or saved — any change after that, manual or autogenerated, counts
const adjustmentBaseline = ref<Record<string, unknown>>({});

// persistable option defs (those the template flagged persisted), deduped by key so a template
// that declares the same option twice does not produce duplicate rows in the save/load prompts
const persistableOptionDefs = computed(() => {
  const seen = new Set<string>();
  return (templateMeta.value.options ?? []).filter((def) => {
    const key = def.properties.key;
    if (!def.properties.persisted || !key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
});

// concrete channel string adjustments scope to, e.g. company:123 (null when not persistable here)
const adjustmentChannel = computed(() =>
  resolveAdjustmentChannel(templateMeta.value, {
    companyId: publication.value?.company_id,
    customerId: publication.value?.customer_id,
  })
);

// name used when the user saves an adjustment without naming it
const suggestedAdjustmentName = computed(() => defaultAdjustmentName(savedAdjustments.value));

const leftColumn = ref<HTMLElement | null>(null);
const rightColumn = ref<HTMLElement | null>(null);
const isDragging = ref(false);
let resizeObserver: ResizeObserver | null = null;

/* ----------------------------- LIFECYCLE HOOKS --------------------------- */
onMounted(() => {
  // the publication is already loaded by this step, nothing to wait for, allow advancing
  allowNext.value = true;

  // put qualification questions up front
  currentTab.value = 'questions';

  getPublicationTemplate(publication.value!.template, publication.value?.variation)
    .then(async (response) => {
      templateMeta.value = response.data;

      const opts: Record<string, unknown> = {};
      for (const def of templateMeta.value.options) {
        const key = def.properties.key;
        if (!key) continue;

        if (publication.value?.template_values?.[key] !== undefined) {
          opts[key] = publication.value.template_values[key];
        } else if (templateMeta.value.fallback_data?.[key] !== undefined) {
          // from/default snapshot — never demo_data, including for autogen misses
          opts[key] = templateMeta.value.fallback_data[key];
        } else if (def.type === 'input.checkbox') {
          opts[key] = false;
        } else if (def.type === 'input.image') {
          opts[key] = null;
        } else if (def.type === 'input.complex') {
          opts[key] = def.properties.schema?.type === 'array' ? [] : {};
        } else {
          opts[key] = '';
        }
      }

      void prefetchCompanyImages();

      // offer to load saved adjustments for this company/customer before the preview renders, so it
      // opens with the chosen adjustment already applied instead of the defaults and then reloading;
      // never auto-applies — the prompt always waits for an explicit choice or dismissal
      const appliedAdjustment = await maybePromptLoadAdjustments();
      adjustmentAppliedKeys = new Set(Object.keys(appliedAdjustment));
      templateOptions.value = { ...opts, ...appliedAdjustment };
      // a loaded adjustment establishes the new "nothing to save" reference; an untouched
      // template (nothing loaded) has nothing accounted for yet, so the baseline stays empty
      if (Object.keys(appliedAdjustment).length > 0) {
        adjustmentBaseline.value = { ...templateOptions.value };
      }
      templateReady.value = true;

      const pubkey = publication.value?.pubkey;
      if (pubkey) {
        void pollPublicationTask(pubkey);
      }
    })
    .catch((error) => {
      console.error('Error fetching template:', error);
    })
    .finally(() => {
      routeReady();
    });

  fetchInteractionModules()
    .then((response) => {
      interactionModules.value = response;
    })
    .catch((error) => {
      console.error('Error fetching interaction modules:', error);
      // an empty set still counts as loaded: the frame reports the state as ready, so the
      // template falls back to placeholders instead of loading forever on a failed fetch
      interactionModules.value = {};
    });

  void nextTick(() => {
    measurePreviewCanvas();

    // observe the canvas so toolbar wrapping and column resizing both update the preview
    if (previewCanvas.value) {
      resizeObserver = new ResizeObserver(measurePreviewCanvas);
      resizeObserver.observe(previewCanvas.value);
    }
  });
});

onUnmounted(() => {
  stopDragging();
  if (isFullscreen.value) {
    document.body.style.overflow = previousBodyOverflow;
  }
  document.removeEventListener('keydown', onFullscreenKeydown);
  if (formTaskPollId !== null) {
    clearTimeout(formTaskPollId);
  }
  resizeObserver?.disconnect();
  resizeObserver = null;
});

/* -------------------------------- COMPUTED -------------------------------- */
const previewWidth = computed(() => {
  if (isFullscreen.value && previewDevice.value === 'desktop') {
    return previewCanvasWidth.value || 1440;
  }
  return { mobile: 390, tablet: 768, desktop: 1440 }[previewDevice.value];
});
const previewScale = computed(() => Math.min(1, previewCanvasWidth.value / previewWidth.value) || 1);
const previewFrameStyle = computed(() => ({
  width: `${previewWidth.value * previewScale.value}px`,
  height: `${previewCanvasHeight.value}px`,
  '--preview-width': `${previewWidth.value}px`,
  '--preview-height': `${previewCanvasHeight.value / previewScale.value}px`,
  '--preview-scale': previewScale.value,
}));

const orderTabs = computed(() => {
  const tabs: { label: string; value: string; isLoading: boolean; badge: string | undefined; badgeClass?: string }[] =
    [];

  tabs.push({
    label: translator.get('publication.editor.tabs.questions'),
    value: 'questions',
    isLoading: questionsLoading.value,
    badge: questionsLoading.value ? undefined : String(formQuestions.value.length),
    badgeClass: 'question-count',
  });

  tabs.push({
    label: translator.get('publication.editor.tabs.settings'),
    value: 'settings',
    isLoading: false,
    badge: undefined,
  });

  tabs.push({
    label: translator.get('publication.editor.tabs.form'),
    value: 'form',
    isLoading: false,
    badge: undefined,
  });

  return tabs;
});

const publicationState = computed(() => {
  const publicationData = publication.value;
  if (!publicationData?.name || !publicationData?.pubkey || !publicationData?.template) {
    return undefined;
  }

  return {
    name: publicationData.name,
    pubkey: publicationData.pubkey,
    template: publicationData.template,
    variation: publicationData.variation,
  };
});

const popsParameters = computed(() => ({
  targetOrigin: window.location.origin,
  template: publication.value?.template,
}));

/* --------------------------------- METHODS -------------------------------- */
function openFullscreen() {
  if (isFullscreen.value) return;
  stopDragging();
  previousPreviewMode = popsMode.value;
  previousPreviewDevice = previewDevice.value;
  previousBodyOverflow = document.body.style.overflow;
  popsMode.value = 'preview';
  previewDevice.value = 'desktop';
  isFullscreen.value = true;
  document.body.style.overflow = 'hidden';
  document.addEventListener('keydown', onFullscreenKeydown);
  void nextTick(() => {
    measurePreviewCanvas();
    rightColumn.value?.querySelector<HTMLButtonElement>('[data-testid="publication-editor-fullscreen-close"]')?.focus();
  });
}

function closeFullscreen() {
  if (!isFullscreen.value) return;
  isFullscreen.value = false;
  popsMode.value = previousPreviewMode;
  previewDevice.value = previousPreviewDevice;
  document.body.style.overflow = previousBodyOverflow;
  document.removeEventListener('keydown', onFullscreenKeydown);
  void nextTick(() => {
    measurePreviewCanvas();
    // the toolbar recreates its normal controls when fullscreen closes
    rightColumn.value?.querySelector<HTMLButtonElement>('[data-testid="publication-editor-fullscreen-open"]')?.focus();
  });
}

function onFullscreenKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault();
    closeFullscreen();
  }
}

function filterTemplateOptions(
  includeField: (def: TemplateOptionDef, header: TemplateOptionDef | null) => boolean
): TemplateOptionDef[] {
  const result: TemplateOptionDef[] = [];
  let currentHeader: TemplateOptionDef | null = null;
  let currentFields: TemplateOptionDef[] = [];

  const flushSection = () => {
    const fields = currentFields.filter((field) => includeField(field, currentHeader));
    if (fields.length > 0) {
      if (currentHeader) {
        result.push(currentHeader);
      }
      result.push(...fields);
    }

    currentHeader = null;
    currentFields = [];
  };

  for (const def of templateMeta.value.options ?? []) {
    if (def.type === 'section.header') {
      flushSection();
      currentHeader = def;
      continue;
    }

    currentFields.push(def);
  }

  flushSection();
  return result;
}

function toggleFormSection(section: 'contact') {
  formSectionExpanded.value = {
    ...formSectionExpanded.value,
    [section]: !formSectionExpanded.value[section],
  };
}

async function updatePublication(stopLoading?: () => void): Promise<boolean> {
  if (publicationSavingState.value === 'saving') {
    return false;
  }

  setSavingState();
  try {
    const { company, customer, order, ...rest } = publication.value!;
    rest.template_values = { ...templateOptions.value };
    const updatedPublication = await updateCustomerPublication(route.query['pubkey'] as string, rest);
    publication.value = { ...updatedPublication.data, company, customer, order };
    setSavedState(); // setSavedState already holds the indicator for 600ms before clearing
    return true;
  } catch (error) {
    console.error('Error updating publication:', error);
    setErrorState(translator.get('publication.editor.update_failed'));
    return false;
  } finally {
    if (stopLoading) stopLoading(); // ensure button loading stops
  }
}

async function updateContactMethod(value: string) {
  if (!publication.value || !['email', 'phone', 'both'].includes(value)) {
    return;
  }

  const previousMethod = publication.value.form_contact_method;
  publication.value.form_contact_method = value as FormContactMethod;
  setSavingState();

  try {
    const updatedPublication = await updateCustomerPublication(route.query['pubkey'] as string, {
      form_contact_method: publication.value.form_contact_method,
    });
    publication.value = { ...publication.value, ...updatedPublication.data };
    setSavedState();
  } catch (error) {
    publication.value.form_contact_method = previousMethod;
    console.error('Error updating publication contact method:', error);
    setErrorState(translator.get('publication.editor.update_failed'));
  }
}

function measurePreviewCanvas() {
  const canvas = previewCanvas.value;
  if (!canvas) return;
  const style = getComputedStyle(canvas);
  previewCanvasWidth.value = Math.max(
    0,
    canvas.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0)
  );
  previewCanvasHeight.value = Math.max(
    0,
    canvas.clientHeight - (parseFloat(style.paddingTop) || 0) - (parseFloat(style.paddingBottom) || 0)
  );
}

function startDragging(event: MouseEvent) {
  event.preventDefault();
  isDragging.value = true;
  if (publicationIframe.value) {
    publicationIframe.value.classList.add('dragging');
  }
  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDragging);
}

function stopDragging() {
  isDragging.value = false;
  if (publicationIframe.value) {
    publicationIframe.value.classList.remove('dragging');
  }
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDragging);
}

function onDrag(event: MouseEvent) {
  event.preventDefault();
  if (!isDragging.value || !leftColumn.value || !rightColumn.value) return;

  const container = leftColumn.value.parentElement;
  if (!container) return;

  const containerRect = container.getBoundingClientRect();
  const newLeftWidth = event.clientX - containerRect.left;

  // drag between 20% and 80% of the container
  const minWidth = containerRect.width * 0.2;
  const maxWidth = containerRect.width * 0.8;

  if (newLeftWidth > minWidth && newLeftWidth < maxWidth) {
    leftColumn.value.style.width = `${newLeftWidth}px`;
    rightColumn.value.style.width = `${containerRect.width - newLeftWidth - 1}px`;
  }

  measurePreviewCanvas();
}

// helper functions for saving state management
function setSavingState() {
  allowNext.value = false;
  publicationSavingState.value = 'saving';
}
function setSavedState(successMessage?: string) {
  if (successMessage) {
    toast.success(successMessage);
  }
  allowNext.value = true;
  publicationSavingState.value = 'saved';
  // keep the saving indicator visible for at least 600ms so it doesn't flicker
  const minDelay = new Promise((resolve) => setTimeout(resolve, 600));
  void minDelay.then(() => {
    if (publicationSavingState.value === 'saved') {
      publicationSavingState.value = 'idle';
    }
  });
}
function setErrorState(errorMessage?: string) {
  if (errorMessage) {
    toast.error(errorMessage);
  }
  publicationSavingState.value = 'error';
  allowNext.value = false;
  setTimeout(() => {
    if (publicationSavingState.value === 'error') {
      publicationSavingState.value = 'idle';
    }
  }, 3000);
}

async function pollPublicationTask(pubkey: string): Promise<void> {
  optimizingPhase.value = 'transform_content';
  isOptimizingContent.value = true;
  return new Promise((resolve) => {
    const poll = async () => {
      try {
        const result = await getPublicationTaskStatus(pubkey);

        if (result.task === null) {
          // no task dispatched — nothing to wait for
          isOptimizingContent.value = false;
          resolve();
          return;
        }

        if (result.task.status_string === 'success') {
          // merge the result options over the current values; the PopsHostFrame watcher
          // picks up the change and live-patches the iframe without a full reload.
          // skip keys the user explicitly set via a loaded adjustment — the generated
          // content must not clobber a choice the user already made.
          if (result.data && Object.keys(result.data).length > 0) {
            const merged = { ...templateOptions.value };
            for (const [key, value] of Object.entries(result.data)) {
              if (!adjustmentAppliedKeys.has(key)) merged[key] = value;
            }
            // autogenerated content is a change like any other — it stays diffed against
            // adjustmentBaseline, so it can trigger the save prompt just as a manual edit would
            templateOptions.value = merged;
          }
          isOptimizingContent.value = false;
          resolve();
          return;
        }

        if (result.task.status_string === 'error') {
          isOptimizingContent.value = false;
          resolve();
          return;
        }

        // still in progress — poll again in 3 seconds
        formTaskPollId = setTimeout(poll, 3000);
      } catch (error) {
        console.error('Error polling transform_content task status:', error);
        isOptimizingContent.value = false;
        resolve();
      }
    };
    void poll();
  });
}

function cancelPendingSave() {
  if (saveDebounce) {
    clearTimeout(saveDebounce);
    saveDebounce = null;
  }
}

function debounceSave() {
  if (saveDebounce) clearTimeout(saveDebounce);
  saveDebounce = setTimeout(updatePublication, 1000);
}

async function persistPublication(): Promise<boolean> {
  cancelPendingSave();
  return updatePublication();
}

function expandSectionForKey(key: string) {
  formOptionsFormRef.value?.expandSectionForKey(key);
  settingsOptionsFormRef.value?.expandSectionForKey(key);
}

function expandFormSection(section: 'contact') {
  formSectionExpanded.value = { ...formSectionExpanded.value, [section]: true };
}

const questionsUnavailable: QuestionMutationResult = {
  success: false,
  error: 'Form questions are not available on this publication.',
};

async function createQuestion(input: FormQuestionInput): Promise<QuestionMutationResult> {
  if (!questionEditorRef.value) return questionsUnavailable;
  return questionEditorRef.value.createQuestion(input);
}

async function updateQuestion(id: number, input: FormQuestionInput): Promise<QuestionMutationResult> {
  if (!questionEditorRef.value) return questionsUnavailable;
  return questionEditorRef.value.updateQuestion(id, input);
}

async function deleteQuestion(id: number): Promise<QuestionMutationResult> {
  if (!questionEditorRef.value) return questionsUnavailable;
  return questionEditorRef.value.deleteQuestion(id);
}

async function reorderQuestions(ids: number[]): Promise<QuestionMutationResult> {
  if (!questionEditorRef.value) return questionsUnavailable;
  return questionEditorRef.value.reorderQuestions(ids);
}

async function prefetchCompanyImages() {
  const hasImageField = (templateMeta.value.options ?? []).some((def) => def.type === 'input.image');
  const id = companyId.value;
  if (!hasImageField || !id) return;

  try {
    const response = await fetchCompanyImages(id);
    companyImages.value = (response.data ?? [])
      .filter((img) => !!img.hash)
      .map((img) => ({
        hash: img.hash,
        url: img.proxy?.url_thumbnail ?? img.proxy?.url_preview ?? '',
      }));
  } catch (error) {
    console.error('Error fetching company images:', error);
    companyImages.value = [];
  }
}
function onClientOptionsUpdate(opts: Record<string, unknown>) {
  templateOptions.value = opts;
  debounceSave();
}

// 'preview-form' is the rename target, dual-emitted so a template still listening only
// for the old 'preview-funnel' event (an already-cached bundle) keeps reacting to the button.
function onPreview() {
  popsHostRef.value?.emitToTemplate('preview-funnel');
  popsHostRef.value?.emitToTemplate('preview-form');
}
function onOptionChange({ key, value, forcesRefresh }: { key: string; value: unknown; forcesRefresh: boolean }) {
  popsHostRef.value?.patchOption(key, value);

  if (forcesRefresh) {
    if (reloadDebounce) clearTimeout(reloadDebounce);
    reloadDebounce = setTimeout(() => popsHostRef.value?.reload(), 300);
  }

  debounceSave();
}

/* --------------------------- TEMPLATE ADJUSTMENTS ------------------------- */
async function loadSavedAdjustments(channel: string) {
  try {
    const response = await fetchPublicationAdjustments(channel);
    savedAdjustments.value = response.data ?? [];
  } catch (error) {
    console.error('Error fetching saved adjustments:', error);
  }
}

// fetch and prompt to load saved adjustments; resolves with the values to apply (empty when there is
// nothing to offer, or the user declines/closes the prompt without picking one). runs every time a
// template is opened — a fresh selection always deserves a fresh prompt, even for the same channel
async function maybePromptLoadAdjustments(): Promise<Record<string, unknown>> {
  const channel = adjustmentChannel.value;
  if (!channel || persistableOptionDefs.value.length === 0) {
    return {};
  }

  await loadSavedAdjustments(channel);
  if (!savedAdjustments.value.length) {
    return {};
  }

  return new Promise((resolve) => {
    loadAdjustmentResolver = resolve;
    showLoadAdjustmentModal.value = true;
  });
}

// record the chosen adjustment's values (only keys known to this template); the watcher below
// resolves the pending maybePromptLoadAdjustments() promise once the modal closes
function onLoadAdjustment(adjustment: IPublicationAdjustment) {
  const knownKeys = new Set(
    (templateMeta.value.options ?? []).map((def) => def.properties.key).filter(Boolean) as string[]
  );

  const applied: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(adjustment.values ?? {})) {
    if (knownKeys.has(key)) applied[key] = value;
  }

  pendingAppliedAdjustment = applied;
  toast.success(translator.get('publication.editor.adjustments.load_success'));
}

async function onDeleteAdjustment(adjustment: IPublicationAdjustment) {
  try {
    await deletePublicationAdjustment(adjustment.id);
    savedAdjustments.value = savedAdjustments.value.filter((item) => item.id !== adjustment.id);
    toast.success(translator.get('publication.editor.adjustments.delete_success'));
    if (savedAdjustments.value.length === 0) {
      showLoadAdjustmentModal.value = false;
    }
  } catch (error) {
    console.error('Error deleting adjustment:', error);
    toast.error(translator.get('publication.editor.adjustments.delete_error'));
  }
}

// called by the parent wizard before leaving the editor step; resolves once the save prompt is
// handled (or immediately when there is nothing persistable to offer)
async function confirmAdjustmentsBeforeLeave(): Promise<boolean> {
  const channel = adjustmentChannel.value;
  // nothing to offer unless a persistable option differs from what was last saved or loaded
  // (or, for an untouched template, from empty)
  const hasChangedValue = hasChangedFromBaseline(
    persistableOptionDefs.value,
    templateOptions.value,
    adjustmentBaseline.value
  );

  if (!channel || !hasChangedValue) {
    return true;
  }

  // refresh the list first so the suggested name continues the existing numbering
  await loadSavedAdjustments(channel);

  return new Promise((resolve) => {
    saveAdjustmentResolver = resolve;
    showSaveAdjustmentModal.value = true;
  });
}

async function onSaveAdjustment(payload: { name: string; values: Record<string, unknown> }, stopLoading: () => void) {
  const channel = adjustmentChannel.value;
  if (!channel || !publication.value?.template) {
    stopLoading();
    showSaveAdjustmentModal.value = false;
    return;
  }

  try {
    const response = await createPublicationAdjustment({
      name: payload.name,
      channel,
      template: publication.value.template,
      variation: publication.value.variation ?? null,
      values: payload.values,
    });
    // keep the local list current so a second save this session keeps counting up
    if (response.data) savedAdjustments.value = [...savedAdjustments.value, response.data];
    // these values are now persisted — nothing left to offer until something changes again
    adjustmentBaseline.value = { ...templateOptions.value };
    toast.success(translator.get('publication.editor.adjustments.save_success'));
  } catch (error) {
    console.error('Error saving adjustment:', error);
    toast.error(translator.get('publication.editor.adjustments.save_error'));
  } finally {
    stopLoading();
    showSaveAdjustmentModal.value = false; // the watcher below resolves the pending promise
  }
}

// dismissed via the X button — the user just wants the modal gone, not to leave the step
function onCloseAdjustmentModal() {
  showSaveAdjustmentModal.value = false;
  if (saveAdjustmentResolver) {
    saveAdjustmentResolver(false);
    saveAdjustmentResolver = null;
  }
}

// resolve the pending leave-promise once the save prompt closes via an explicit decision (save or skip);
// a plain X dismiss is handled separately above and must not let the wizard proceed
watch(showSaveAdjustmentModal, (open) => {
  if (!open && saveAdjustmentResolver) {
    saveAdjustmentResolver(true);
    saveAdjustmentResolver = null;
  }
});

// resolve the pending load-promise however the load prompt closes (load, decline, or backdrop/X)
watch(showLoadAdjustmentModal, (open) => {
  if (!open && loadAdjustmentResolver) {
    loadAdjustmentResolver(pendingAppliedAdjustment);
    loadAdjustmentResolver = null;
    pendingAppliedAdjustment = {};
  }
});

defineExpose({ confirmAdjustmentsBeforeLeave });

useAutopilotWizardStep(() => ({
  key: 'publication_editor',
  title: translator.get('publication.editor.title'),
  description:
    'The user edits the generated online publication (content, form questions, settings) with a live preview. Everything on screen is in context.current_publication; follow context.publication_workflow.',
}));

useAutopilotPublicationEditor({
  publication,
  templateOptions,
  templateMeta,
  currentTab,
  popsMode,
  formQuestions,
  questionsLoading,
  publicationSavingState,
  isOptimizingContent,
  optimizingPhase,
  companyImages,
  cancelPendingSave,
  patchOption: (key, value) => popsHostRef.value?.patchOption(key, value),
  reloadPreview: () => popsHostRef.value?.reload(),
  persist: persistPublication,
  updateContactMethod,
  expandSectionForKey,
  expandFormSection,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  reorderQuestions,
});
</script>

<style scoped>
.publication-editor-step.is-fullscreen {
  position: fixed;
  inset: 0;
  height: 100dvh;
  z-index: 1100;
  background: #fff;
}

.is-fullscreen .resizable-container,
.is-fullscreen .right-column {
  height: 100%;
}

.is-fullscreen .right-column {
  /* override the inline width retained from dragging the divider */
  width: 100% !important;
}

.is-fullscreen-desktop .publication-preview-canvas {
  padding: 0;
}

.is-fullscreen-desktop .publication-preview-iframe {
  border-radius: 0;
  box-shadow: none;
}

.resizable-container {
  /* ------- Editor requires a fixed height to calculate the editor area ------ */
  height: calc(100vh - 188px);
  position: relative;
}

.left-column {
  background-color: #fff;
  width: 25%;
  min-width: min(360px, 50%);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.left-column .sticky-tabs-header {
  min-height: 54px;
  flex-shrink: 0;
  width: 100%;
  display: flex;
  align-items: end;
}

.left-column .sticky-tabs-header .nav-bordered {
  width: 100%;
}

.sticky-tabs-header :deep(.question-count) {
  background: #eff4ff !important;
  color: var(--tblr-primary) !important;
}

.questions-panel {
  padding: 24px 20px;
  background: #f9fafb;
  flex: 1;
  min-height: 0;
}

.content-area {
  flex: 1;
  overflow-y: auto;
  /* Vertical scrolling */
  -ms-overflow-style: none;
  /* Hide scrollbar in IE and Edge */
  scrollbar-width: none;
  /* Hide scrollbar in Firefox */
}

.content-area::-webkit-scrollbar {
  display: none;
  /* Hide scrollbar in Chrome, Safari */
}

.form-editor-section {
  --section-padding-x: 1.5rem;
  margin-inline: calc(-1 * var(--section-padding-x));
  padding-block: 0.75rem;
  border-bottom: 1px solid var(--tblr-border-color, #e6e7e9);
  transition: background-color 0.15s ease;
}

.form-editor-section:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.form-options:deep(.template-options-section:last-child) {
  border-bottom: 1px solid var(--tblr-border-color, #e6e7e9);
}

.section-header,
.section-body {
  padding-inline: 1.5rem;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  padding-top: 0;
  padding-bottom: 0;
  margin-bottom: 0.75rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
}

.section-header-text {
  flex: 1;
}

.section-header-chevron {
  flex-shrink: 0;
  margin-top: 0.125rem;
  color: var(--tblr-muted, #6c757d);
}

.right-column {
  container: publication-preview / inline-size;
  width: 75%;
  min-width: 0;
  position: sticky;
  top: 0;
  align-self: flex-start;
  height: calc(100vh - 188px);
}

.divider {
  position: relative;
  width: 1px;
  flex-shrink: 0;
  background-color: #eaecf0;
  cursor: col-resize;
  user-select: none;
  transition: background-color 0.2s;
}

.divider:hover,
.divider:active {
  background-color: #c8c8c8;
}

.divider-handle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border: 1px solid #eaecf0;
  border-radius: 6px;
  color: #98a2b3;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.06);
  cursor: col-resize;
  z-index: 2;
}

.divider-handle:hover {
  color: #475467;
  border-color: #d0d5dd;
}

.iframe-container {
  position: relative;
  overflow: hidden;
  background-color: #ffffff;
}

.publication-content {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.publication-preview-canvas {
  flex: 1;
  min-height: 0;
  padding: 20px;
  overflow: hidden;
  background: #f2f4f7;
}

.publication-preview-iframe {
  margin-inline: auto;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border-radius: 6px;
  background: #fff;
  box-shadow:
    0 0 0 1px #e4e7ec,
    0 4px 16px rgb(16 24 40 / 6%);
}

.publication-preview-iframe :deep(.pops-host-iframe) {
  display: block;
  position: relative;
  z-index: 0;
  width: var(--preview-width);
  height: var(--preview-height);
  max-width: none;
  transform: scale(var(--preview-scale));
  transform-origin: top left;
}

.publication-preview-loading {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* while dragging the divider, disable pointer events on the preview so the iframe
   doesn't swallow mousemove/mouseup — otherwise stopDragging never fires and the
   handle stays stuck to the cursor after release */
.publication-preview-iframe.dragging {
  pointer-events: none;
}

.optimize-content-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 2rem;
  z-index: 2;
  pointer-events: none;
}

.optimize-content-indicator {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4rem 0.85rem;
  background: #fff;
  border: 1px solid var(--tblr-border-color, #e6e7e9);
  border-radius: 99px;
  font-size: 0.8125rem;
  color: var(--tblr-secondary, #6c757d);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.optimize-overlay-enter-active,
.optimize-overlay-leave-active {
  transition: opacity 0.2s ease;
}

.optimize-overlay-enter-from,
.optimize-overlay-leave-to {
  opacity: 0;
}
</style>
